import { FC, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Breadcrumb, BreadcrumbTemplateEnum, Breadcrumbs, Page, PageHeader, PageInner, PageSection } from "@app/shared";
import { QESButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ModalLink } from "@app/shared/Modals";
import { Specialty, SpecialtyGroup } from "@app/admin/manage-taxonomies";
import SpecialtyService from "../manage-specialties/specialtyService";
import { TemplateDetailsData } from "../manage-specialties";
import { CodeMapSetService } from "@app/codemaps";
import {
  AddCodeMaps,
  CodeMapSetModel, CodeMapTypeEnum
} from "@app/codemaps/CodeMapSetModels";
import React from "react";

interface TaxonomiesMatchProps {
  clientId?: string;
  specialtyCode?: string;
  templateCollectionId?: string;
  templateId?: string;
}

interface SpecialtyToTaxonomy {
  specialtyNameAndCode: string;
  taxonomyCode: string;
}

type TaxonomiesProps = RouteComponentProps<TaxonomiesMatchProps, undefined>;

export const Taxonomies: FC<React.PropsWithChildren<TaxonomiesProps>> = (props: TaxonomiesProps) => {
  const breadcrumbs: (string | Breadcrumb)[] = ['Manage Taxonomies'];

  const specialtyService = new SpecialtyService();
  const codeMapService = new CodeMapSetService();
  const [specialtyGroups, setSpecialtyGroups] = useState<Specialty[]>([]);
  const [codeMaps, setCodeMaps] = useState<CodeMapSetModel[]>([]);
  const [selectedCodeMap, setSelectedCodeMap] = useState<string>('');
  const [specialtyToTaxonomy, setSpecialtyToTaxonomy] = useState<SpecialtyToTaxonomy[]>([]);
  const [codeMapSetId, setCodeMapSetId] = useState<number>(0);
  const [codeMapSetVersionId, setCodeMapSetVersionId] = useState<number>(0);
  const [validForm, setValidForm] = useState<boolean>(true);
  const [codeMapAdd, setCodeMapAdd] = useState<boolean>(false);
  const [codeMapEdit, setCodeMapEdit] = useState<boolean>(false);
  const [selectedSpecialtyGroup, setSelectedSpecialtyGroup] = useState<string>('');
  const [taxonomyCodeList, setTaxonomyCodeList] = useState<string[]>([]);
  const [initialSpecialtyToTaxonomy, setInitialSpecialtyToTaxonomy] = useState<SpecialtyToTaxonomy[]>([]);

  const GetCodeMaps = () => {
    codeMapService.getOnlyClientCodeMaps(CodeMapTypeEnum.SpecialtyCodeMap).then(data => {
      setCodeMaps(data);
    });
  }
  const getSpecialtyData = async () => {
    if (selectedCodeMap !== '') {
      const data: TemplateDetailsData = await specialtyService.getTemplateDetails(Number(props.match.params.templateCollectionId), props.match.params.templateId);
      const filteredCodeMap = codeMaps.flatMap(x => ({
        ...x,
        codeMapSetVersions: x.codeMapSetVersions.filter(item => item.codeMapSetVersionStatus === 'activated')
      }));
      const codeMapSetId = filteredCodeMap.find((x) => x.resource.name === selectedCodeMap).codeMapSetId;
      setCodeMapSetId(codeMapSetId);
      setCodeMapSetVersionId(filteredCodeMap.find((x) => x.resource.name === selectedCodeMap).codeMapSetVersions[0].codeMapSetVersionId);
      const taxonomyList = await codeMapService.getCodeMapItems(codeMapSetId);
      const categoryList = data.categories.flatMap((cat) => cat.specialtyGroups);
      const specialtyGroupList: SpecialtyGroup[] = categoryList.map((sg) => ({
        code: sg.code,
        name: sg.name,
        specialties: sg.specialties.map((s) => ({
          code: s.code,
          name: s.name,
          taxonomies: taxonomyList.filter(function (taxonomy) {
            return taxonomy.codeMapValue === s.code
          }).map((x) => ({
            codeMapValue: x.codeMapValue,
            sourceValue: x.sourceValue
          }))
        }))
      }));

      setSpecialtyGroups(specialtyGroupList.flatMap((x) => x.specialties).filter((specialty, index, specialtyList) =>
        index === specialtyList.findIndex((o) => o.code === specialty.code)));
      setInitialSpecialtyToTaxonomy([...new Set(specialtyGroupList.flatMap((x) => x.specialties.flatMap((s) => s.taxonomies.map((t) => ({
        specialtyNameAndCode: `${s.name} - ${t.codeMapValue}`,
        taxonomyCode: t.sourceValue
      })))))]);
      setSpecialtyToTaxonomy([...new Set(specialtyGroupList.flatMap((x) => x.specialties.flatMap((s) => s.taxonomies.map((t) => ({
        specialtyNameAndCode: `${s.name} - ${t.codeMapValue}`,
        taxonomyCode: t.sourceValue
      })))))]);
    }
  }

  const deleteTaxonomy = (t: string) => {
    taxonomyCodeList.splice(taxonomyCodeList.indexOf(t), 1);
    setTaxonomyCodeList([...taxonomyCodeList]);
    checkValidForm();
  }

  const deleteEditTaxonomy = (t: SpecialtyToTaxonomy) => {
    specialtyToTaxonomy.splice(specialtyToTaxonomy.indexOf(t), 1);
    setSpecialtyToTaxonomy([...specialtyToTaxonomy]);
    checkValidForm();
  }

  useEffect(() => {
    GetCodeMaps();
  }, []);

  useEffect(() => {
    getSpecialtyData();
  }, [selectedCodeMap]);

  const onSpecialtyChange = (e: string) => {
    setSelectedSpecialtyGroup(e);
    checkValidForm();
  }

  const onCodeMapAdd = () => {
    taxonomyCodeList.push('');
    setTaxonomyCodeList([...taxonomyCodeList]);
    checkValidForm();
  }

  const taxonomyCodeUpdate = (e: string, t: string) => {
    taxonomyCodeList.splice((taxonomyCodeList.findIndex((x) => x === t)), 1, e.toUpperCase());
    setTaxonomyCodeList([...taxonomyCodeList]);
    checkValidForm();
  }

  const taxonomyCodeEditUpdate = (e: string, t: SpecialtyToTaxonomy) => {
    specialtyToTaxonomy.find((x) => x.specialtyNameAndCode === t.specialtyNameAndCode && x.taxonomyCode === t.taxonomyCode).taxonomyCode = e.toUpperCase();
    setSpecialtyToTaxonomy([...specialtyToTaxonomy]);
    checkValidForm();
  }

  const cancel = () => {
    const path = window.location.pathname;
    const templatePieces = path.split('/');
    window.location.href = `/app/v2.0/admin/manage-templates/manage-specialties/specialties/${templatePieces[5]}/${templatePieces[6]}`;
  }

  const checkValidForm = () => {
    setValidForm(specialtyToTaxonomy.every((x) => x.taxonomyCode.length > 0 && x.specialtyNameAndCode != ''));
    if (codeMapAdd) {
      setValidForm(taxonomyCodeList.every((x) => x.length > 0));
    }
  }

  const save = async () => {
    if (codeMapAdd) {
      taxonomyCodeList.forEach(element => {
        const newCodeMap: SpecialtyToTaxonomy = {
          specialtyNameAndCode: selectedSpecialtyGroup,
          taxonomyCode: element
        }
        specialtyToTaxonomy.push(newCodeMap);
      });
    }
    const addCodeMaps: AddCodeMaps = {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      codeMapItems: specialtyToTaxonomy.flatMap((x) => ({
        codeMapValue: x.specialtyNameAndCode.split(' - ').pop(),
        sourceValue: x.taxonomyCode
      }))
    };
    await codeMapService.AddCodeMapSetVersion(codeMapSetId).then((data) =>
      codeMapService.ArchiveCodeMapSetVersion(codeMapSetId, codeMapSetVersionId).then(() =>
        codeMapService.AddCodeMapItems(codeMapSetId, data.codeMapSetVersionId, addCodeMaps)).then(() =>
          codeMapService.ActivateCodeMapSetVersion(codeMapSetId, data.codeMapSetVersionId).then(() => {
            const path = window.location.pathname;
            const templatePieces = path.split('/');
            window.location.href = `/app/v2.0/admin/manage-templates/manage-specialties/specialties/${templatePieces[5]}/${templatePieces[6]}`;
          })
        )
    );
  };

  return (
    <>
      <Page documentTitle="Manage Taxonomies - Admin">
        <Breadcrumbs
          template={BreadcrumbTemplateEnum.ManageTemplates}
          items={breadcrumbs}
        />
        <PageInner>
          <PageHeader title="Taxonomy Crosswalks" iconName={QesIcons.FontAwesome.Sellsy}>
          </PageHeader>
          <p className="subheader">Here you can manage the Taxonomy information for Specialty Codes.</p>
          <PageSection>
            <QESSelect
              style={{ width: "400px" }}
              label={"Select Taxonomy Code Map to Edit"}
              testId="taxonomyselect"
              onChange={(e) => setSelectedCodeMap(e.target.value)}
              value={selectedCodeMap}
            >
              {codeMaps.map((r, index) => (
                <option key={index} value={r.resource.name}>{r.resource.name}</option>
              ))}
            </QESSelect>
            <div className="mt-3">
              <QESButton
                disabled={selectedCodeMap === ''}
                className="mr-2"
                qesType="primary"
                qesTestId="add-taxonomy__add-button"
                onClick={() => {
                  setCodeMapAdd(true);
                  setCodeMapEdit(false);
                  setSpecialtyToTaxonomy(initialSpecialtyToTaxonomy);
                }}>
                Add Taxonomy Code
              </QESButton>
              <QESButton
                disabled={selectedCodeMap === ''}
                qesType="primary"
                qesTestId="edit-taxonomy__edit-button"
                onClick={() => {
                  setCodeMapEdit(true);
                  setCodeMapAdd(false);
                  setTaxonomyCodeList([]);
                }}>
                Edit Taxonomy Code
              </QESButton>
            </div>
            <div className="row mt-3">
              <div className="col-12" style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start" }} >
                <div style={{ minWidth: "100%", paddingLeft: "3%", paddingRight: "2%" }}>
                  {codeMapAdd &&
                    <React.Fragment>
                      <div className="row mb-2 mr-2 mt-4">
                        <div className="col-4 mr-2">
                          <h6>Input Specialty Code</h6>
                        </div>
                        <div className="col-3 mr-2">
                          <h6>Input Taxonomy Code</h6>
                        </div>
                      </div>
                      <div className="row mb-2 mr-3">
                        <div className="col-4 mr-3">
                          <QESSelect
                            label="Specialty Name - Code"
                            hideLabel={true}
                            testId="select-specialty__group-value"
                            value={selectedSpecialtyGroup}
                            onChange={(e) => onSpecialtyChange(e.target.value)}
                          >
                            {specialtyGroups.map((r) => (
                              <option key={r.name} >{r.name} - {r.code}</option>
                            ))}
                          </QESSelect>
                        </div>
                        <div className="flex-col mr-2">
                          {taxonomyCodeList.map((t, index) =>
                            <>
                              <div className="col-30 mb-2">
                                <QESTextInput
                                  className="flex-col"
                                  label="Taxonomy Code"
                                  hideLabel={true}
                                  required={true}
                                  errorText=""
                                  showCounter={false}
                                  isValid={t.length > 0}
                                  key={index}
                                  testId="edit-taxonomy__source-value"
                                  value={t.toUpperCase()}
                                  onChange={(e) => taxonomyCodeUpdate(e.target.value, t)}
                                />
                              </div>
                            </>
                          )}
                          {selectedSpecialtyGroup !== '' &&
                            <QESButton
                              className=""
                              qesType="secondary"
                              qesTestId="edit-specialty__edit-button"
                              onClick={() => onCodeMapAdd()}>
                              Add Taxonomy Code
                            </QESButton>
                          }
                        </div>
                        <div className="flex-col">
                          {taxonomyCodeList.map((t) =>
                            <>
                              <div className="col-3 mb-2">
                                <ModalLink
                                  classNames="qes-button-secondary danger"
                                  content={{
                                    body: "Are you sure you want to delete this taxonomy crosswalk?",
                                    cancelButtonText: "Cancel",
                                    confirmButtonAction: () => deleteTaxonomy(t),
                                    confirmButtonText: "Delete",
                                    confirmButtonType: "primary-danger",
                                    title: `Delete Taxonomy Crosswalk`,
                                  }}
                                  testId="manage-taxonomy__delete-taxonomy-modal"
                                  hideBodyScroll={true}
                                >
                                  Delete
                                </ModalLink>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                    </React.Fragment>
                  }
                  {codeMapEdit &&
                    <>
                      <div className="col-4 mr-2 mt-4">
                        <h6>Select Specialty to Edit</h6>
                      </div>
                      <div className="col-4 mr-3">
                        <QESSelect
                          label="Specialty Name - Code"
                          hideLabel={true}
                          testId="edit-taxonomy__source-value"
                          value={selectedSpecialtyGroup}
                          onChange={(e) => onSpecialtyChange(e.target.value)}
                        >
                          {specialtyGroups.map((r) => (
                            <option key={r.name} >{r.name} - {r.code}</option>
                          ))}
                        </QESSelect>
                      </div>
                      {selectedSpecialtyGroup !== '' &&
                        <div className="col-4 mr-2 mt-4">
                          <h6>Taxonomy Codes</h6>
                        </div>
                      }
                      <div>
                        {selectedSpecialtyGroup !== '' && specialtyToTaxonomy.filter((x) => x.specialtyNameAndCode
                          === selectedSpecialtyGroup).map((t, index) => {
                            return (
                              <div className="row ml-1 mr-2 col-4 mb-1" key={index}>
                                <QESTextInput
                                  label="Taxonomy Code"
                                  hideLabel={true}
                                  maxLength={10}
                                  required={true}
                                  errorText=""
                                  showCounter={false}
                                  isValid={t.taxonomyCode.length > 0}
                                  key={t.specialtyNameAndCode}
                                  testId="edit-taxonomy__source-value"
                                  value={t.taxonomyCode.toUpperCase()}
                                  onChange={(e) => taxonomyCodeEditUpdate(e.target.value, t)}
                                />
                                <div className="col-3 mb-2">
                                  <ModalLink
                                    classNames="qes-button-secondary danger"
                                    content={{
                                      body: "Are you sure you want to delete this taxonomy crosswalk?",
                                      cancelButtonText: "Cancel",
                                      confirmButtonAction: () => deleteEditTaxonomy(t),
                                      confirmButtonText: "Delete",
                                      confirmButtonType: "primary-danger",
                                      title: `Delete Taxonomy Crosswalk`,
                                    }}
                                    testId="manage-taxonomy__delete-taxonomy-modal"
                                    hideBodyScroll={true}
                                  >
                                    Delete
                                  </ModalLink>
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    </>
                  }
                </div>
              </div>
            </div>
          </PageSection>
          {(codeMapAdd || codeMapEdit) &&
            <div className="mt-4 d-flex flex-row">
              <ModalLink
                classNames="qes-button-secondary danger ml-5 mr-5"
                content={{
                  body: "Are you sure you want to undo your changes to this taxonomy crosswalk?",
                  cancelButtonText: "Go Back",
                  confirmButtonAction: () => cancel(),
                  confirmButtonText: "Cancel",
                  confirmButtonType: "primary-danger",
                  title: `Cancel Taxonomy Crosswalk Updates`,
                }}
                testId="manage-taxonomy__delete-taxonomy-modal"
                hideBodyScroll={true}
              >
                Cancel
              </ModalLink>
              <ModalLink
                classNames="qes-button-secondary ml-5"
                content={{
                  body: "Are you sure you want to save this taxonomy crosswalk?",
                  cancelButtonText: "Go Back",
                  confirmButtonAction: () => save(),
                  confirmButtonText: "Save Changes",
                  confirmButtonType: "primary",
                  title: `Save Taxonomy Crosswalk Updates`,
                }}
                testId="manage-taxonomy__save-taxonomy-modal"
                hideBodyScroll={true}
                disabled={!validForm}
              >
                Save Changes
              </ModalLink>
            </div>
          }
        </PageInner>
      </Page >
    </>

  );
}
